import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  Alert, 
  CircularProgress, 
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Stack,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  AlertTitle,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormHelperText,
  Autocomplete
} from '@mui/material';
import { 
  Upload, 
  Download, 
  Refresh, 
  FileUpload, 
  MonetizationOn,
  CheckCircle,
  ErrorOutline,
  Warning,
  Info,
  Close,
  ExpandMore,
  ExpandLess,
  Save,
  Cancel,
  Add,
  Edit,
  Delete,
  MoreVert,
  Sync
} from '@mui/icons-material';
import { ProjectSyncCard, newSyncRequestId } from '../Common/ProjectSyncCard';
import { TreasuryDataGrid } from '../DataGrid/TreasuryDataGrid';
import { 
  GridColDef, 
  GridRenderCellParams
} from '@mui/x-data-grid';
import { ProjectStatusBadge } from '../Common/ProjectStatusBadge';
import { Project, ProyectoItemCompat, SucursalOption } from '../../types';
import { getProyectosCatalogo, ProyectoCatalogo } from '../../utils/apiService';

interface ActualizarProyectosResultProps {
  initialData?: ProyectoItemCompat[];
  syncCsrfToken?: string;
  syncIsAdmin?: boolean;
  syncAllowedProjectCodes?: number[];
}

// Sucursal options for dropdown
const SUCURSAL_OPTIONS: SucursalOption[] = [
  { value: 'Antioquia', label: 'Antioquia' },
  { value: 'Cundinamarca', label: 'Cundinamarca' },
  { value: 'Caribe', label: 'Caribe' }
];

// Status options for dropdown
const STATUS_OPTIONS = [
  { value: true, label: 'Activo' },
  { value: false, label: 'Inactivo' }
];

// Tipo Centro Costos options for dropdown
const TIPO_CENTRO_COSTOS_OPTIONS = [
  { value: 'Operativo', label: 'Operativo' },
  { value: 'Preoperativo', label: 'Preoperativo' }
];





export const ActualizarProyectosResult: React.FC<ActualizarProyectosResultProps> = ({ 
  initialData = [],
  syncCsrfToken = '',
  syncIsAdmin = false,
  syncAllowedProjectCodes = []
}) => {
  const [syncPermissions, setSyncPermissions] = useState({
    es_admin: syncIsAdmin, centros_asignados: syncAllowedProjectCodes,
  });
  const [syncProject, setSyncProject] = useState<{ codigo: number; nombre: string; requestId?: string } | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  const listRequest = useRef(0);
  const creationAttempt = useRef<{ payload: string; id: string } | null>(null);
  const savingProject = useRef(false);
  // State management
  const [proyectos, setProyectos] = useState<ProyectoItemCompat[]>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning', message: string, details?: string[] } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    severity?: 'warning' | 'error' | 'info';
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    severity: 'warning'
  });
  
  // Enhanced validation and error handling state
  const [showValidationDetails, setShowValidationDetails] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [fileValidationError, setFileValidationError] = useState<string | null>(null);
  
  // CRUD operation states
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [addDialog, setAddDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingProject, setEditingProject] = useState<ProyectoItemCompat | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; rows: string[] }>({ open: false, rows: [] });
  const [newProject, setNewProject] = useState<Partial<ProyectoItemCompat>>({
    'Codigo Centro Costos': 0,
    'Nombre Proyecto': '',
    'Tipo Centro Costos': 'Operativo',
    'Sucursal': 'Antioquia',
    active: true
  });
  
  // Project catalog state for searchable project selector
  const [projectCatalog, setProjectCatalog] = useState<any[]>([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  


  // Reset form when dialog closes
  const resetProjectDialog = () => {
    creationAttempt.current = null;
    setAddDialog(false);
    setEditMode(false);
    setEditingProject(null);
    setNewProject({
      'Codigo Centro Costos': 0,
      'Nombre Proyecto': '',
      'Tipo Centro Costos': 'Operativo',
      'Sucursal': 'Antioquia',
      active: true
    });
    setSelectedProject(null);
  };

  const handleCloseAddDialog = () => {
    if (!savingProject.current) resetProjectDialog();
  };

  // Open modal in edit mode
  const handleEditProject = (project: ProyectoItemCompat) => {
    setEditMode(true);
    setEditingProject(project);
    setNewProject({
      'Codigo Centro Costos': project['Codigo Centro Costos'],
      'Nombre Proyecto': project['Nombre Proyecto'],
      'Tipo Centro Costos': project['Tipo Centro Costos'],
      'Sucursal': project['Sucursal'],
      active: project.active
    });
    
    // Find and set the selected project from catalog
    const catalogProject = projectCatalog.find(p => p.nombre_proyecto === project['Nombre Proyecto']);
    setSelectedProject(catalogProject || null);
    
    setAddDialog(true);
  };

  // Load project catalog for searchable selector
  const loadProjectCatalog = async (sucursal?: string) => {
    setIsLoadingCatalog(true);
    try {
      const params = new URLSearchParams();
      if (sucursal) params.append('sucursal', sucursal);
      params.append('activo_only', 'true'); // Only show active projects
      
      const response = await fetch(`/api/v1/proyectos-catalogo?${params.toString()}`);
      if (!response.ok) throw new Error('Error al cargar catálogo de proyectos');
      
      const data = await response.json();
      if (data.success) {
        setProjectCatalog(data.projects);
      } else {
        console.error('Error loading project catalog:', data.message);
        setProjectCatalog([]);
      }
    } catch (error) {
      console.error('Error loading project catalog:', error);
      setProjectCatalog([]);
    } finally {
      setIsLoadingCatalog(false);
    }
  };

  // Load initial data
  useEffect(() => {
    if (proyectos.length === 0) {
      loadProyectos();
    }
    // Load project catalog on component mount
    loadProjectCatalog();
  }, []);

  // Reload catalog when sucursal changes in the form or when entering edit mode
  useEffect(() => {
    if (newProject.Sucursal) {
      loadProjectCatalog(newProject.Sucursal);
    }
  }, [newProject.Sucursal, editMode]);



  const loadProyectos = async () => {
    const version = ++listRequest.current;
    setIsLoading(true);
    try {
      // Use the enhanced API endpoint that includes active status
      const response = await fetch('/api/v1/configuracion_proyectos');
      if (!response.ok) throw new Error('Error al cargar proyectos');
      const data = await response.json();

      // Parse the proyectos JSON string from the API response
      const projectsArray = JSON.parse(data.proyectos);
      
      // Transform data to ensure proper IDs and match ProyectoItemCompat interface
      const transformedData: ProyectoItemCompat[] = projectsArray.map((item: any, index: number) => ({
        id: `proyecto_${index}_${item['Codigo Centro Costos']}`,
        'Codigo Centro Costos': item['Codigo Centro Costos'],
        'Nombre Proyecto': item['Nombre Proyecto'],
        'Tipo Centro Costos': item['Tipo Centro Costos'],
        'Sucursal': item['Sucursal'] || 'Antioquia', // Default to Antioquia for backward compatibility
        'Fiducia': item['Fiducia'] || '', // Resolved by the nightly sync; empty until then
        'Codigo ADPRO': item['Codigo ADPRO'] || '', // Refreshed by the nightly sync; empty until then
        'Ultima Actualizacion Sistema': item['Ultima Actualizacion Sistema'],
        'Ultima Actualizacion Usuario': item['Ultima Actualizacion Usuario'],
        active: item.active !== undefined ? item.active : true // Default to true for backward compatibility
      }));

      if (version !== listRequest.current) return false;
      setProyectos(transformedData);
      setSelectedRows([]);
      return true;
    } catch (error) {
      console.error('Error loading proyectos:', error);
      if (version === listRequest.current) setAlert({ type: 'error', message: 'Error al cargar los proyectos' });
      return false;
    } finally {
      if (version === listRequest.current) setIsLoading(false);
    }
  };

  const handleUploadClick = () => {
    if (proyectos.length > 0) {
      setConfirmDialog({
        open: true,
        title: 'Confirmar Actualización',
        message: 'Esta acción actualizará los proyectos existentes y puede desactivar proyectos que no estén en el archivo. ¿Desea continuar?',
        onConfirm: () => {
          setConfirmDialog(prev => ({ ...prev, open: false }));
          setUploadDialog(true);
          setSelectedFile(null);
        }
      });
    } else {
      setUploadDialog(true);
      setSelectedFile(null);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileValidationError(null);
    
    if (file) {
      const validation = validateFile(file);
      if (validation.isValid) {
        setSelectedFile(file);
      } else {
        setFileValidationError(validation.errors.join('. '));
        setSelectedFile(null);
        // Clear the input
        event.target.value = '';
      }
    } else {
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!allowedTypes.includes(selectedFile.type)) {
        throw new Error('Tipo de archivo no válido. Solo se permiten archivos Excel (.xlsx, .xls)');
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (selectedFile.size > maxSize) {
        throw new Error('El archivo es demasiado grande. Tamaño máximo permitido: 10MB');
      }

      const formData = new FormData();
      formData.append('file', selectedFile);

      // Call Flask route instead of API directly
      const response = await fetch('/actualizar-proyectos', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',  // Tell Flask we want JSON response
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `Error HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // If response is not JSON, use the status text
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      if (result.success) {
        const successMessage = result.projects_processed !== undefined 
          ? `Proyectos actualizados exitosamente. ${result.projects_processed} procesados, ${result.projects_upserted || 0} actualizados/insertados, ${result.projects_deactivated || 0} desactivados.`
          : result.message || 'Proyectos actualizados exitosamente';
        
        setAlert({ 
          type: 'success', 
          message: successMessage
        });
      } else {
        setAlert({ type: 'error', message: result.message || 'Error al actualizar proyectos' });
      }
      
      setUploadDialog(false);
      setSelectedFile(null);
      
      // Reload data to show updated status
      await loadProyectos();
    } catch (error) {
      console.error('Error uploading file:', error);
      setAlert({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Error inesperado al actualizar los proyectos' 
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadExcel = () => {
    window.open('/download-proyectos-excel', '_blank');
  };

  const handleRefresh = async () => {
    setIsManualRefreshing(true);
    try {
      if (await loadProyectos()) setAlert({ type: 'success', message: 'Proyectos actualizados exitosamente' });
    } finally {
      setIsManualRefreshing(false);
    }
  };

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'Codigo Centro Costos',
      headerName: 'Código Centro Costos',
      width: 180,
      filterable: true,
      type: 'number'
    },
    {
      field: 'Nombre Proyecto',
      headerName: 'Nombre Proyecto Docuware',
      width: 250,
      filterable: true,
    },
    {
      field: 'Tipo Centro Costos',
      headerName: 'Tipo Centro Costos',
      width: 180,
      filterable: true,
    },
    {
      field: 'Sucursal',
      headerName: 'Sucursal',
      width: 150,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        const sucursal = params.value || 'Antioquia';
        const colorMap = {
          'Antioquia': { bg: 'success.main', color: 'success.contrastText' },
          'Cundinamarca': { bg: 'info.main', color: 'info.contrastText' },
          'Caribe': { bg: 'warning.main', color: 'warning.contrastText' }
        };
        const colors = colorMap[sucursal as keyof typeof colorMap] || { bg: 'grey.500', color: 'white' };
        
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              px: 1,
              backgroundColor: colors.bg,
              color: colors.color,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              textAlign: 'center',
              minHeight: 24,
            }}
          >
            {sucursal}
          </Box>
        );
      }
    },
    {
      field: 'Fiducia',
      headerName: 'Fiducia',
      width: 240,
      filterable: true,
      // Read-only on purpose: the sync resolves this from the MANDANTE of the project's
      // latest approved SINCO lot. It is absent from the create/edit dialog, and the API
      // ignores `fiducia` on POST/PUT, so this is presentation only -- not the guarantee.
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        const fiducia = (params.value ?? '').toString().trim();
        return (
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              color: fiducia ? 'text.primary' : 'text.disabled',
              fontStyle: fiducia ? 'normal' : 'italic',
            }}
          >
            {fiducia || 'Sin resolver'}
          </Typography>
        );
      }
    },
    {
      field: 'Codigo ADPRO',
      headerName: 'Código ADPRO',
      width: 160,
      filterable: true,
      // Read-only on purpose, and hidden by default -- see columnVisibilityModel below.
      // The sync refreshes this from SINCO's ADPRO project master; the API ignores it on
      // POST/PUT, so `editable: false` is presentation only, not the guarantee.
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        const codigos = (params.value ?? '').toString().trim();
        return (
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              color: codigos ? 'text.primary' : 'text.disabled',
              fontStyle: codigos ? 'normal' : 'italic',
            }}
          >
            {codigos || 'Sin resolver'}
          </Typography>
        );
      }
    },
    {
      field: 'active',
      headerName: 'Estado',
      width: 120,
      filterable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        // Convert the row data to Project interface for the badge
        const project: Project = {
          'Codigo Centro Costos': params.row['Codigo Centro Costos'],
          'Nombre Proyecto': params.row['Nombre Proyecto'],
          'Tipo Centro Costos': params.row['Tipo Centro Costos'],
          'Ultima Actualizacion Sistema': params.row['Ultima Actualizacion Sistema'],
          'Ultima Actualizacion Usuario': params.row['Ultima Actualizacion Usuario'],
          active: params.row.active
        };
        
        return (
          <ProjectStatusBadge
            project={project}
            size="small"
            showText={true}
            showIcon={true}
          />
        );
      },
      valueGetter: (value, row) => row.active ? 'Activo' : 'Inactivo'
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={!params.row.active ? 'El proyecto está inactivo'
              : !syncPermissions.es_admin && !syncPermissions.centros_asignados.includes(Number(params.row['Codigo Centro Costos']))
                ? 'No tiene asignado este proyecto' : 'Sincronización del proyecto'}>
              <span>
                <IconButton size="small" color="primary" aria-label={`Sincronizar proyecto ${params.row['Codigo Centro Costos']}`}
                  disabled={!params.row.active || (!syncPermissions.es_admin
                    && !syncPermissions.centros_asignados.includes(Number(params.row['Codigo Centro Costos'])))}
                  onClick={() => {
                    // Reopening the same project preserves the mounted watcher.
                    setSyncProject(previous => previous?.codigo === Number(params.row['Codigo Centro Costos'])
                      ? previous : { codigo: Number(params.row['Codigo Centro Costos']), nombre: params.row['Nombre Proyecto'] });
                    setSyncDialogOpen(true);
                  }}><Sync fontSize="small" /></IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Editar proyecto">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEditProject(params.row as ProyectoItemCompat)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar proyecto">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteProjects([params.id.toString()])}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    },
    {
      field: 'Ultima Actualizacion Sistema',
      headerName: 'Última Actualización Sistema',
      width: 200,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        const date = params.value;
        if (!date || date === 'None') return '-';
        
        try {
          return new Date(date).toLocaleDateString('es-CO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch {
          return date;
        }
      }
    },
    {
      field: 'Ultima Actualizacion Usuario',
      headerName: 'Última Actualización Usuario',
      width: 200,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        const date = params.value;
        if (!date || date === 'None') return '-';
        
        try {
          return new Date(date).toLocaleDateString('es-CO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch {
          return date;
        }
      }
    }
  ];

  const closeAlert = () => setAlert(null);

  // Auto-dismiss success alerts after 5 seconds
  useEffect(() => {
    if (alert && alert.type === 'success') {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // CRUD Operations
  const handleAddProject = async () => {
    if (savingProject.current) return;
    try {
      // Validate project data
      const tempProject: ProyectoItemCompat = {
        id: editMode ? editingProject!.id : `temp_${Date.now()}`,
        'Codigo Centro Costos': newProject['Codigo Centro Costos'] || 0,
        'Nombre Proyecto': newProject['Nombre Proyecto'] || '',
        'Tipo Centro Costos': newProject['Tipo Centro Costos'] || '',
        'Sucursal': newProject['Sucursal'] || 'Antioquia',
        'Ultima Actualizacion Sistema': editMode ? editingProject!['Ultima Actualizacion Sistema'] : new Date().toISOString(),
        'Ultima Actualizacion Usuario': new Date().toISOString(),
        active: newProject.active !== undefined ? newProject.active : true
      };

      const validation = validateProjectUpdate(tempProject);
      if (!validation.isValid) {
        setAlert({
          type: 'error',
          message: editMode ? 'Errores de validación en la edición del proyecto' : 'Errores de validación en el nuevo proyecto',
          details: validation.errors
        });
        return;
      }

      const payload = {
        codigo_centro_costos: tempProject['Codigo Centro Costos'],
        nombre_proyecto: tempProject['Nombre Proyecto'],
        tipo_centro_costos: tempProject['Tipo Centro Costos'],
        sucursal: tempProject['Sucursal'], active: tempProject.active,
      };
      if (!editMode) {
        const serialized = JSON.stringify(payload);
        if (creationAttempt.current?.payload !== serialized) {
          creationAttempt.current = { payload: serialized, id: newSyncRequestId() };
        }
      }
      savingProject.current = true;
      setIsSavingProject(true);
      // Call appropriate API endpoint
      const url = editMode ? `/api/v1/proyectos/${editingProject!['Codigo Centro Costos']}` : '/api/proyectos';
      const method = editMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          ...(!editMode ? { 'X-CSRF-Token': syncCsrfToken } : {}),
        },
        body: JSON.stringify(editMode ? payload : { ...payload, solicitud_id: creationAttempt.current.id }),
      });

      if (!response.ok) {
        let errorMessage = `Error HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error || errorData.message || errorData.detail) {
            errorMessage = errorData.error || errorData.message || errorData.detail;
          }
        } catch {
          // If response is not JSON, use the status text
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        ++listRequest.current; // Ignore list reads that began before this mutation committed.
        setIsLoading(false);
        if (editMode) {
          // Update existing project in the list
          setProyectos(prev => prev.map(p => 
            p.id === editingProject!.id ? { ...tempProject } : p
          ));
          setSuccessSnackbar({
            open: true,
            message: 'Proyecto actualizado exitosamente'
          });
        } else {
          // Add new project to the list
          const newProjectWithId = {
            ...tempProject,
            ...result.updated_project,
            id: `proyecto_${proyectos.length}_${tempProject['Codigo Centro Costos']}`
          };
          
          setProyectos(prev => [...prev, newProjectWithId]);
          if (result.permisos_sincronizacion) setSyncPermissions(result.permisos_sincronizacion);
          if (result.sincronizacion_id) {
            setSyncProject({ codigo: tempProject['Codigo Centro Costos'], nombre: tempProject['Nombre Proyecto'],
              requestId: result.sincronizacion_id });
            setSyncDialogOpen(true);
          }
          setSuccessSnackbar({
            open: true,
            message: result.sincronizacion_id ? 'Proyecto creado. Sincronización en cola.'
              : 'Proyecto creado inactivo. No se inicia la sincronización.'
          });
        }
        
        // Reset form and close dialog
        resetProjectDialog();
      } else {
        throw new Error(result.message || (editMode ? 'Error al actualizar el proyecto' : 'Error al crear el proyecto'));
      }
    } catch (error) {
      console.error(editMode ? 'Error updating project:' : 'Error creating project:', error);
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : (editMode ? 'Error inesperado al actualizar el proyecto' : 'Error inesperado al crear el proyecto')
      });
    } finally {
      savingProject.current = false;
      setIsSavingProject(false);
    }
  };

  const handleDeleteProjects = async (rowIds: string[]) => {
    try {
      const projectsToDelete = proyectos.filter(p => rowIds.includes(p.id));
      
      // Show confirmation
      setDeleteDialog({
        open: true,
        rows: rowIds
      });
    } catch (error) {
      console.error('Error preparing delete:', error);
      setAlert({
        type: 'error',
        message: 'Error al preparar la eliminación'
      });
    }
  };

  const confirmDeleteProjects = async () => {
    setIsDeletingProject(true);
    try {
      const rowIds = deleteDialog.rows;
      const projectsToDelete = proyectos.filter(p => rowIds.includes(p.id));
      
      // Delete each project
      const deletePromises = projectsToDelete.map(async (project) => {
        const response = await fetch(`/api/v1/proyectos/${project['Codigo Centro Costos']}`, {
          method: 'DELETE',
        });
        
        if (!response.ok) {
          throw new Error(`Error al eliminar proyecto ${project['Codigo Centro Costos']}`);
        }
        
        return response.json();
      });

      await Promise.all(deletePromises);
      
      ++listRequest.current;
      setIsLoading(false);
      // Remove from local state
      setProyectos(prev => prev.filter(p => !rowIds.includes(p.id)));
      setSelectedRows([]);
      setDeleteDialog({ open: false, rows: [] });
      
      setSuccessSnackbar({
        open: true,
        message: `${projectsToDelete.length} proyecto(s) eliminado(s) exitosamente`
      });
    } catch (error) {
      console.error('Error deleting projects:', error);
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Error inesperado al eliminar proyectos'
      });
      setDeleteDialog({ open: false, rows: [] });
    } finally {
      setIsDeletingProject(false);
    }
  };

  // Enhanced validation functions for add/edit project modal
  const validateProjectUpdate = (newRow: ProyectoItemCompat): { 
    isValid: boolean; 
    errors: string[]; 
  } => {
    const errors: string[] = [];

    // Validate Código Centro Costos
    if (!newRow['Codigo Centro Costos']) {
      errors.push('Código Centro Costos es requerido');
    } else {
      const codigo = Number(newRow['Codigo Centro Costos']);
      if (isNaN(codigo) || codigo <= 0) {
        errors.push('Código Centro Costos debe ser un número mayor a 0');
      } else if (codigo.toString().length < 3) {
        errors.push('Código Centro Costos debe tener al menos 3 dígitos');
      } else if (codigo > 999999999) {
        errors.push('Código Centro Costos no puede exceder 9 dígitos');
      }
      
      // Check for duplicate codes (skip current project in edit mode)
      const duplicates = proyectos.filter(p => 
        p.id !== newRow.id && p['Codigo Centro Costos'] === newRow['Codigo Centro Costos']
      );
      if (duplicates.length > 0) {
        errors.push(`Código Centro Costos ${codigo} ya existe en el sistema`);
      }
    }

    // Validate Nombre Proyecto
    if (!newRow['Nombre Proyecto'] || newRow['Nombre Proyecto'].trim() === '') {
      errors.push('Nombre Proyecto es requerido');
    } else {
      const nombre = newRow['Nombre Proyecto'].trim();
      if (nombre.length < 3) {
        errors.push('Nombre Proyecto debe tener al menos 3 caracteres');
      } else if (nombre.length > 512) {
        errors.push('Nombre Proyecto no puede exceder 512 caracteres');
      }
      
      // Check for special characters that might cause issues
      const invalidChars = /[<>:"\\|?*]/;
      if (invalidChars.test(nombre)) {
        errors.push('Nombre Proyecto no puede contener caracteres especiales: < > : " \\ | ? *');
      }
    }

    // Validate Tipo Centro Costos - only accept predefined values
    if (!newRow['Tipo Centro Costos'] || newRow['Tipo Centro Costos'].trim() === '') {
      errors.push('Tipo Centro Costos es requerido');
    } else {
      const tipo = newRow['Tipo Centro Costos'].trim();
      const validTipos = ['Operativo', 'Preoperativo'];
      if (!validTipos.includes(tipo)) {
        errors.push('Tipo Centro Costos debe ser "Operativo" o "Preoperativo"');
      }
    }

    // Validate Sucursal
    const validSucursales = ['Antioquia', 'Cundinamarca', 'Caribe'];
    if (!newRow.Sucursal) {
      errors.push('Sucursal es requerido');
    } else if (!validSucursales.includes(newRow.Sucursal)) {
      errors.push(`Sucursal debe ser uno de: ${validSucursales.join(', ')}`);
    }

    // Validate Estado (active)
    if (newRow.active === undefined || newRow.active === null) {
      errors.push('Estado es requerido (Activo/Inactivo)');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // File validation function
  const validateFile = (file: File): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    if (!allowedTypes.includes(file.type)) {
      errors.push('Tipo de archivo no válido. Solo se permiten archivos Excel (.xlsx, .xls)');
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      errors.push('El archivo es demasiado grande. Tamaño máximo permitido: 10MB');
    }

    // Check file name
    if (file.name.length > 255) {
      errors.push('El nombre del archivo es demasiado largo');
    }

    const invalidChars = /[<>:"\\|?*]/;
    if (invalidChars.test(file.name)) {
      errors.push('El nombre del archivo contiene caracteres no válidos');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };





  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Enhanced Alert with detailed error information */}
      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={closeAlert}
          sx={{ mb: 2 }}
          icon={
            alert.type === 'success' ? <CheckCircle /> : 
            alert.type === 'warning' ? <Warning /> :
            alert.type === 'info' ? <Info /> : <ErrorOutline />
          }
          action={
            alert.details && alert.details.length > 0 ? (
              <IconButton
                aria-label="toggle details"
                color="inherit"
                size="small"
                onClick={() => setShowValidationDetails(!showValidationDetails)}
              >
                {showValidationDetails ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            ) : undefined
          }
        >
          <AlertTitle>
            {alert.type === 'error' ? 'Error' : 
             alert.type === 'warning' ? 'Advertencia' : 
             alert.type === 'success' ? 'Éxito' : 'Información'}
          </AlertTitle>
          {alert.message}
          
          {/* Detailed error/warning information */}
          {alert.details && alert.details.length > 0 && (
            <Collapse in={showValidationDetails}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detalles:
                </Typography>
                <List dense>
                  {alert.details.map((detail, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <ErrorOutline fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={detail}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Collapse>
          )}
        </Alert>
      )}

      {/* Success Snackbar for quick feedback */}
      <Snackbar
        open={successSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setSuccessSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSuccessSnackbar({ open: false, message: '' })} 
          severity="success"
          variant="filled"
          icon={<CheckCircle />}
        >
          {successSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Main Card */}
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <MonetizationOn color="success" />
              <Typography variant="h5" component="div">
                Actualizar Centros de Costos por Proyecto
              </Typography>
            </Box>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadExcel}
                size="small"
              >
                Descargar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={isManualRefreshing ? <CircularProgress size={16} /> : <Refresh />}
                onClick={handleRefresh}
                size="small"
                disabled={isManualRefreshing}
              >
                {isManualRefreshing ? 'Actualizando...' : 'Actualizar'}
              </Button>
            </Stack>
          }
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            '& .MuiCardHeader-action': { 
              '& .MuiButton-outlined': {
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }
            }
          }}
        />
        <CardContent>
          {/* Upload Section */}
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  <Upload sx={{ mr: 1 }} />
                  Cargar archivo de Centros de Costos por Proyecto
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  El archivo debe contener las columnas: Codigo Centro Costos, Nombre Proyecto, Tipo Centro Costos
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<FileUpload />}
                  onClick={handleUploadClick}
                  fullWidth
                  disabled={isLoading || isManualRefreshing}
                >
                  Actualizar Centros de Costos por Proyecto
                </Button>
              </Grid>
            </Grid>
          </Paper>



          {/* Project Statistics Summary */}
          {proyectos.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom>
                Resumen de Proyectos
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                      {proyectos.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Proyectos
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {proyectos.filter(p => p.active).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Activos
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {proyectos.filter(p => !p.active).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inactivos
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main" fontWeight="bold">
                      {new Set(proyectos.map(p => p.Sucursal)).size}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sucursales
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Sucursal breakdown */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Distribución por Sucursal:
                </Typography>
                <Grid container spacing={1}>
                  {SUCURSAL_OPTIONS.map(sucursal => {
                    const count = proyectos.filter(p => p.Sucursal === sucursal.value).length;
                    const activeCount = proyectos.filter(p => p.Sucursal === sucursal.value && p.active).length;
                    return (
                      <Grid item xs={4} key={sucursal.value}>
                        <Box sx={{ textAlign: 'center', p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {sucursal.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {activeCount}/{count} activos
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Paper>
          )}



          {/* CRUD Actions Toolbar */}
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MonetizationOn color="primary" />
                Gestión de Proyectos
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Add />}
                  onClick={() => setAddDialog(true)}
                  size="small"
                >
                  Agregar Proyecto
                </Button>
                
                {/* Bulk operations temporarily disabled */}
                
                <Button
                  variant="outlined"
                  startIcon={isManualRefreshing ? <CircularProgress size={16} /> : <Refresh />}
                  onClick={handleRefresh}
                  size="small"
                  disabled={isManualRefreshing}
                >
                  Actualizar
                </Button>
                

              </Box>
            </Box>
            
            {/* Status indicators */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={proyectos.filter(p => p.active).length > proyectos.filter(p => !p.active).length ? <CheckCircle /> : <Warning />}
                label={`${proyectos.filter(p => p.active).length}/${proyectos.length} activos`}
                color={proyectos.filter(p => p.active).length > proyectos.filter(p => !p.active).length ? 'success' : 'warning'}
                variant="outlined"
                size="small"
              />
            </Box>
          </Paper>

          {/* Data Grid */}
          <Box sx={{ mb: 2, position: 'relative' }}>
            {Array.isArray(proyectos) ? (
              <TreasuryDataGrid
              rows={proyectos || []}
              columns={columns}
              getRowId={(row) => row.id || `fallback_${Math.random()}`}
              loading={isLoading || isManualRefreshing}
              keepRowsWhileLoading
              showToolbar={true}
              showExport={true}
              showRefresh={true}
              title="Centros de Costos por Proyecto"
              tableId="centros-costos-proyecto"
              onRefresh={loadProyectos}
              exportOptions={{
                filename: 'centros_costos_proyecto',
                includeHeader: true,
                includeFooter: true,
                includeSummary: true
              }}
              sx={{ 
                minHeight: 400,
                '& .MuiDataGrid-row': {
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                  },
                },
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: {
                  sortModel: [{ field: 'active', sort: 'desc' }], // Show active projects first
                },
                columns: {
                  columnVisibilityModel: {
                    'Sucursal': true, // Show Sucursal column by default
                    'Codigo ADPRO': false, // Reference data: available from the columns panel, off by default
                    'Ultima Actualizacion Sistema': false, // Hide by default to save space
                    'Ultima Actualizacion Usuario': false, // Hide by default to save space
                  },
                },

              }}
              pageSizeOptions={[25, 50, 100]}
              density="compact"
              filterMode="client"
              sortingMode="client"
              paginationMode="client"
              checkboxSelection={false}
              // Accessibility
              aria-label="Tabla de centros de costos por proyecto"
            />
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  Cargando datos...
                </Typography>
                <CircularProgress sx={{ mt: 2 }} />
              </Paper>
            )}
          </Box>
        </CardContent>
      </Card>

      {syncProject && (
        <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} keepMounted maxWidth="md" fullWidth>
          <DialogTitle>Sincronización · {syncProject.nombre} ({syncProject.codigo})</DialogTitle>
          <DialogContent>
            <ProjectSyncCard key={`${syncProject.codigo}:${syncProject.requestId || ''}`}
              codigo={syncProject.codigo} csrfToken={syncCsrfToken} watchOperationId={syncProject.requestId}
              canRefresh={!addDialog && !uploadDialog && !deleteDialog.open && !confirmDialog.open
                && !isSavingProject && !isUploading && !isDeletingProject && !isLoading && !isManualRefreshing}
              onRefresh={loadProyectos} />
          </DialogContent>
          <DialogActions><Button onClick={() => setSyncDialogOpen(false)}>Cerrar</Button></DialogActions>
        </Dialog>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => setUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FileUpload />
            Cargar Archivo de Centros de Costos por Proyecto
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Seleccionar archivo Excel (.xlsx) con los centros de costos por proyecto actualizados
            </Typography>
            <TextField
              type="file"
              fullWidth
              variant="outlined"
              onChange={handleFileSelect}
              inputProps={{
                accept: '.xlsx,.xls',
              }}
              sx={{ mt: 2 }}
              error={!!fileValidationError}
              helperText={fileValidationError || "Seleccione un archivo Excel (.xlsx o .xls) - Tamaño máximo: 10MB"}
            />
            
            {/* File validation error */}
            {fileValidationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Error de Validación del Archivo</AlertTitle>
                {fileValidationError}
              </Alert>
            )}
            
            {/* Selected file information */}
            {selectedFile && !fileValidationError && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'success.light', borderRadius: 1, border: 1, borderColor: 'success.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="bold">
                    Archivo válido seleccionado
                  </Typography>
                </Box>
                <Typography variant="body2">
                  <strong>Nombre:</strong> {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Tamaño:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Tipo:</strong> {selectedFile.type || 'Excel'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Última modificación:</strong> {new Date(selectedFile.lastModified).toLocaleDateString('es-CO')}
                </Typography>
              </Box>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" component="div">
                <strong>Requisitos del archivo:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Formato: Excel (.xlsx o .xls)</li>
                  <li>Tamaño máximo: 10MB</li>
                  <li>Columnas requeridas: Codigo Centro Costos, Nombre Proyecto, Tipo Centro Costos</li>
                  <li>La columna Sucursal es opcional (se asignará Antioquia por defecto)</li>
                </ul>
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadDialog(false)}
            disabled={isUploading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="success"
            disabled={!selectedFile || isUploading || !!fileValidationError}
            startIcon={isUploading ? <CircularProgress size={20} /> : <Upload />}
          >
            {isUploading ? 'Cargando...' : 'Cargar Archivo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Project Dialog */}
      <Dialog
        open={addDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {editMode ? <Edit /> : <Add />}
            {editMode ? 'Editar Proyecto' : 'Agregar Nuevo Proyecto'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Código Centro Costos"
                  type="number"
                  fullWidth
                  required
                  value={newProject['Codigo Centro Costos'] || ''}
                  onChange={(e) => setNewProject(prev => ({
                    ...prev,
                    'Codigo Centro Costos': parseInt(e.target.value) || 0
                  }))}
                  helperText="Código único del centro de costos (mínimo 3 dígitos)"
                  disabled={editMode} // Disable in edit mode to prevent changing primary key
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Sucursal"
                  fullWidth
                  required
                  value={newProject.Sucursal || 'Antioquia'}
                  onChange={(e) => setNewProject(prev => ({
                    ...prev,
                    'Sucursal': e.target.value
                  }))}
                  helperText="Seleccione la sucursal del proyecto"
                >
                  {SUCURSAL_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: 
                              option.value === 'Antioquia' ? 'success.main' :
                              option.value === 'Cundinamarca' ? 'info.main' : 'warning.main'
                          }}
                        />
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12}>
                <Autocomplete
                  options={projectCatalog}
                  getOptionLabel={(option) => option.nombre_proyecto || ''}
                  value={selectedProject}
                  onChange={(event, newValue) => {
                    setSelectedProject(newValue);
                    setNewProject(prev => ({
                      ...prev,
                      'Nombre Proyecto': newValue?.nombre_proyecto || ''
                    }));
                  }}
                  loading={isLoadingCatalog}
                  filterOptions={(options, { inputValue }) => {
                    // Filter by project name (case insensitive)
                    return options.filter(option =>
                      option.nombre_proyecto.toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Nombre Proyecto Docuware"
                      placeholder="Nombre Proyecto Docuware"
                      required
                      helperText={
                        isLoadingCatalog 
                          ? "Cargando proyectos del catálogo..." 
                          : "Nombre Proyecto Docuware"
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingCatalog ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {option.nombre_proyecto}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.sucursal}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  noOptionsText={
                    isLoadingCatalog 
                      ? "Cargando..." 
                      : projectCatalog.length === 0 
                        ? "No hay proyectos disponibles en el catálogo"
                        : "No se encontraron proyectos que coincidan"
                  }
                  clearOnBlur={false}
                  selectOnFocus
                  handleHomeEndKeys
                />
              </Grid>
              
              <Grid item xs={12} md={8}>
                <TextField
                  select
                  label="Tipo Centro Costos"
                  fullWidth
                  required
                  value={newProject['Tipo Centro Costos'] || ''}
                  onChange={(e) => setNewProject(prev => ({
                    ...prev,
                    'Tipo Centro Costos': e.target.value
                  }))}
                  helperText="Seleccione el tipo de centro de costos"
                >
                  {TIPO_CENTRO_COSTOS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: option.value === 'Operativo' ? 'primary.main' : 'secondary.main'
                          }}
                        />
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Estado"
                  fullWidth
                  value={newProject.active ? 'true' : 'false'}
                  onChange={(e) => setNewProject(prev => ({
                    ...prev,
                    active: e.target.value === 'true'
                  }))}
                  helperText="Estado del proyecto"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value.toString()} value={option.value.toString()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: option.value ? 'success.main' : 'error.main'
                          }}
                        />
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 3 }}>
              <AlertTitle>Información</AlertTitle>
              {editMode 
                ? 'Los cambios al proyecto serán validados antes de ser guardados. El código del centro de costos no puede ser modificado.'
                : 'El nuevo proyecto será validado antes de ser creado. Asegúrese de que el código del centro de costos sea único.'
              }
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAddDialog}
            disabled={isSavingProject}
            color="inherit"
            startIcon={<Cancel />}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAddProject}
            disabled={isSavingProject}
            variant="contained"
            color="success"
            startIcon={<Save />}
          >
            {isSavingProject ? 'Guardando…' : editMode ? 'Guardar Cambios' : 'Crear Proyecto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, rows: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Delete color="error" />
            Confirmar Eliminación
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            ¿Está seguro de que desea eliminar {deleteDialog.rows.length} proyecto(s)?
          </Typography>
          
          {deleteDialog.rows.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Proyectos a eliminar:
              </Typography>
              <List dense>
                {deleteDialog.rows.map(rowId => {
                  const project = proyectos.find(p => p.id === rowId);
                  return project ? (
                    <ListItem key={rowId}>
                      <ListItemIcon>
                        <Delete fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${project['Codigo Centro Costos']} - ${project['Nombre Proyecto']}`}
                        secondary={`${project['Tipo Centro Costos']} (${project.Sucursal})`}
                      />
                    </ListItem>
                  ) : null;
                })}
              </List>
            </Box>
          )}
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>Advertencia</AlertTitle>
            Esta acción no se puede deshacer. Los proyectos eliminados no podrán ser recuperados.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, rows: [] })}
            color="inherit"
            startIcon={<Cancel />}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDeleteProjects}
            variant="contained"
            color="error"
            startIcon={<Delete />}
          >
            Eliminar Proyectos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {confirmDialog.severity === 'error' ? <ErrorOutline color="error" /> :
             confirmDialog.severity === 'warning' ? <Warning color="warning" /> :
             <Info color="info" />}
            {confirmDialog.title}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {confirmDialog.message}
          </Typography>
          

        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            color="inherit"
            startIcon={<Cancel />}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            variant="contained"
            color={confirmDialog.severity === 'error' ? 'error' : 'warning'}
            startIcon={
              confirmDialog.severity === 'error' ? <ErrorOutline /> :
              confirmDialog.severity === 'warning' ? <Warning /> :
              <Save />
            }
          >
            {confirmDialog.severity === 'error' ? 'Forzar Acción' : 'Continuar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActualizarProyectosResult;